import { SUBSCRIPTION_BASE_URL } from "../utils/ApiUrls";


class SubscriptionServices {

    /**
     * Create a new subscription for an organization
     * @param {string} accessToken - JWT access token
     * @param {Object} subscriptionData - Subscription data
     * @param {number} subscriptionData.organization_id - Organization ID (required)
     * @param {string} subscriptionData.start_date - Start date in ISO format (required)
     * @param {string} subscriptionData.end_date - End date in ISO format (required)
     * @param {string} subscriptionData.reason - Reason for creating subscription (optional)
     * @returns {Promise} Promise resolving to subscription creation result
     */
    async createSubscription(accessToken, subscriptionData) {
        if (!subscriptionData.organization_id || !subscriptionData.start_date || !subscriptionData.end_date) {
            throw new Error("organization_id, start_date, and end_date are required");
        }

        try {
            const response = await fetch(`${SUBSCRIPTION_BASE_URL}/create/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
                body: JSON.stringify(subscriptionData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Failed to create subscription (${response.status})`);
            }

            console.log("✅ Subscription created:", data);
            return { success: true, status: 201, data };

        } catch (error) {
            console.error("❌ Creation failed:", error);
            return { success: false, status: 500, error: error.message };
        }
    }


    /**
     * Update an existing subscription
     * @param {string} accessToken - JWT access token
     * @param {Object} updateData - Subscription update data
     * @param {number} updateData.organization_id - Organization ID (required)
     * @param {string} updateData.end_date - New end date in ISO format (optional)
     * @param {string} updateData.status - New status: active|expired|cancelled|suspended (optional)
     * @param {string} updateData.reason - Reason for update (optional)
     * @returns {Promise} Promise resolving to subscription update result
     */
    async updateSubscription(accessToken, updateData) {
        if (!updateData.organization_id) {
            throw new Error("organization_id is required");
        }

        try {
            const response = await fetch(`${SUBSCRIPTION_BASE_URL}/update/`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
                body: JSON.stringify(updateData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Failed to update subscription (${response.status})`);
            }

            console.log("✅ Subscription updated:", data);
            return { success: true, data };

        } catch (error) {
            console.error("❌ Update failed:", error);
            return { success: false, error: error.message };
        }
    }


    /**
     * Get list of all subscriptions with filtering and pagination
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters (optional)
     * @param {string} params.status - Filter by status: active|expired|cancelled|suspended
     * @param {string} params.search - Search by organization name
     * @param {string} params.ordering - Sort order: newest|oldest|expiring_soon
     * @param {number} params.page - Page number for pagination
     * @param {number} params.page_size - Number of items per page
     * @returns {Promise} Promise resolving to paginated subscription list
     */
    getAllSubscriptions(accessToken, params = {}) {
        console.log('📋 Fetching subscriptions with params:', params);
        
        const queryParams = new URLSearchParams();
        
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);
        if (params.ordering) queryParams.append('ordering', params.ordering);
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        
        const queryString = queryParams.toString();
        const url = `${SUBSCRIPTION_BASE_URL}/list/${queryString ? `?${queryString}` : ''}`;
        
        return fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(err => {
                        const errorMessage = err.error || `Failed to fetch subscriptions (${response.status})`;
                        throw new Error(errorMessage);
                    }).catch(() => {
                        throw new Error(`Failed to fetch subscriptions (${response.status})`);
                    });
                }
                return response.json();
            })
            .then(data => {
                console.log('✅ Subscriptions fetched successfully:', data);
                return {
                    success: true,
                    status: 200,
                    data: data
                };
            })
            .catch(error => {
                console.error('❌ Error fetching subscriptions:', error);
                return {
                    success: false,
                    status: 500,
                    error: error.message
                };
            });
    }

    /**
     * Get detailed information about a specific subscription
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters
     * @param {number} params.id - Subscription ID (optional)
     * @param {number} params.organization_id - Organization ID (optional)
     * Note: Either id or organization_id must be provided
     * @returns {Promise} Promise resolving to detailed subscription info with recent history
     */
    getSubscriptionDetails(accessToken, params) {
        console.log('🔍 Fetching subscription details with params:', params);
        
        // Validate required fields
        if (!params.id && !params.organization_id) {
            return Promise.reject(new Error('Either id or organization_id must be provided'));
        }
        
        const queryParams = new URLSearchParams();
        if (params.id) queryParams.append('id', params.id);
        if (params.organization_id) queryParams.append('organization_id', params.organization_id);
        
        const url = `${SUBSCRIPTION_BASE_URL}/details/?${queryParams.toString()}`;
        
        return fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(err => {
                        const errorMessage = err.error || `Failed to fetch subscription details (${response.status})`;
                        throw new Error(errorMessage);
                    }).catch(() => {
                        throw new Error(`Failed to fetch subscription details (${response.status})`);
                    });
                }
                return response.json();
            })
            .then(data => {
                console.log('✅ Subscription details fetched successfully:', data);
                return {
                    success: true,
                    status: 200,
                    data: data
                };
            })
            .catch(error => {
                console.error('❌ Error fetching subscription details:', error);
                return {
                    success: false,
                    status: 500,
                    error: error.message
                };
            });
    }

    /**
     * Get full subscription history for an organization
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters
     * @param {number} params.organization_id - Organization ID (required)
     * @param {string} params.action - Filter by action type: subscribe|unsubscribe|expire|reactivate|etc. (optional)
     * @param {number} params.page - Page number for pagination (optional)
     * @param {number} params.page_size - Number of items per page (optional)
     * @returns {Promise} Promise resolving to paginated subscription history
     */
    getSubscriptionHistory(accessToken, params) {
        console.log('📚 Fetching subscription history with params:', params);
        
        // Validate required fields
        if (!params.organization_id) {
            return Promise.reject(new Error('organization_id is required'));
        }
        
        const queryParams = new URLSearchParams();
        queryParams.append('organization_id', params.organization_id);
        if (params.action) queryParams.append('action', params.action);
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        
        const url = `${SUBSCRIPTION_BASE_URL}/history/?${queryParams.toString()}`;
        
        return fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(err => {
                        const errorMessage = err.error || `Failed to fetch subscription history (${response.status})`;
                        throw new Error(errorMessage);
                    }).catch(() => {
                        throw new Error(`Failed to fetch subscription history (${response.status})`);
                    });
                }
                return response.json();
            })
            .then(data => {
                console.log('✅ Subscription history fetched successfully:', data);
                return {
                    success: true,
                    status: 200,
                    data: data
                };
            })
            .catch(error => {
                console.error('❌ Error fetching subscription history:', error);
                return {
                    success: false,
                    status: 500,
                    error: error.message
                };
            });
    }

    /**
     * Helper method to format date for API calls
     * @param {Date|string} date - Date to format
     * @returns {string} ISO formatted date string
     */
    formatDateForAPI(date) {
        if (!date) return null;
        
        if (typeof date === 'string') {
            // If it's already a string, ensure it's in the right format
            if (date.includes('T')) {
                return date; // Already in ISO format
            } else {
                // Assume it's a date string like "2024-01-01"
                return date + 'T00:00:00Z';
            }
        }
        
        if (date instanceof Date) {
            return date.toISOString();
        }
        
        return null;
    }

    /**
     * Helper method to get subscription status display text
     * @param {string} status - Subscription status
     * @returns {string} Display text for status
     */
    getStatusDisplayText(status) {
        const statusMap = {
            'active': 'Actif',
            'expired': 'Expiré',
            'cancelled': 'Annulé',
            'suspended': 'Suspendu'
        };
        
        return statusMap[status] || status;
    }

    /**
     * Helper method to get action display text
     * @param {string} action - Subscription action
     * @returns {string} Display text for action
     */
    getActionDisplayText(action) {
        const actionMap = {
            'subscribe': 'Abonnement',
            'unsubscribe': 'Désabonnement',
            'renew': 'Renouvellement',
            'extend': 'Extension',
            'expire': 'Expiration',
            'reactivate': 'Réactivation',
            'cancel': 'Annulation',
            'suspend': 'Suspension'
        };
        
        return actionMap[action] || action;
    }

    /**
     * Helper method to calculate days remaining
     * @param {string} endDate - End date in ISO format
     * @returns {number} Days remaining (0 if expired)
     */
    calculateDaysRemaining(endDate) {
        if (!endDate) return 0;
        
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return Math.max(0, diffDays);
    }

    /**
     * Helper method to check if subscription is expiring soon
     * @param {string} endDate - End date in ISO format
     * @param {number} warningDays - Number of days before expiration to warn (default: 15)
     * @returns {boolean} True if expiring soon
     */
    isExpiringSoon(endDate, warningDays = 15) {
        const daysRemaining = this.calculateDaysRemaining(endDate);
        return daysRemaining > 0 && daysRemaining <= warningDays;
    }
}

const subscriptionServices = new SubscriptionServices();
export default subscriptionServices;